import React, { useCallback, useEffect, useRef, useState } from "react";
import InputText from "../../../../components/inputs/InputText";
import {
  createProduct,
  findProductById,
  searchProductBrands,
  searchProductCategories,
  searchProductSubCategories,
  updateProductById,
  linkProductToDose,
  unlinkProductFromDose,
} from "../../../../api/inventoryService";
import { toast } from "react-toastify";
import PrescriptionLinkPicker, { DoseLink } from "./PrescriptionLinkPicker";
import { isMedicineScale } from "../../productDoseLink";
import { productCategoryIds } from "../../productCategory";
import InputDropSearch from "../../../../components/inputs/InputDropSearch";
import InputSelect from "../../../../components/inputs/InputSelect";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Pack,
  setDefaultPack,
  filledPacks,
  findInvalidPack,
} from "../../inventoryPackUtils";
import { scales } from "../../../../data/scale/scale";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../redux/loader/loaderActions";
interface FormData {
  brandId: string;
  packs: Pack[];
  scale: string;
  categoryId: string;
  subCategoryId: string;
  productName: string;
  alertQuantity: number;
  quantity: number;
  price: number;
  wholePrice: number;
}
interface AddProductProps {
  closeModal: () => void;
  title?: string;
  id?: string;
}

const AddProduct: React.FC<AddProductProps> = ({
  closeModal,
  id,
}) => {
  const [formData, setFormData] = useState<FormData>({
    brandId: "",
    categoryId: "",
    subCategoryId: "",
    productName: "",
    alertQuantity: 0,
    price: 0,
    quantity: 1,
    wholePrice: 0,
    scale: "",
    packs: [],
  });
  const [errors, setErrors] = useState(false);
  const [link, setLink] = useState<DoseLink | null>(null);
  // The link as loaded, so Update only touches it when it changed.
  const [savedLink, setSavedLink] = useState<DoseLink | null>(null);
  // Tablets and capsules saved without a drug get one "are you sure?".
  const [unlinkedWarning, setUnlinkedWarning] = useState(false);
  const allowUnlinked = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const handleChange = useCallback(
    (e: any) => {
      const { name, value } = e.target;

      setFormData((prevData) => {
        if (name === 'quantity' || name === 'wholePrice') {
          const newValue = Number(value);
          
          // Avoid division by zero
          const newPrice =
            name === 'quantity'
              ? newValue === 0
                ? 0
                : prevData.wholePrice / newValue
              : newValue === 0
                ? 0
                : newValue / prevData.quantity;
  
          return {
            ...prevData,
            [name]: newValue,
            price: newPrice,
          };
        }
  
        return { ...prevData, [name]: value };
      });
    },
    [] 
  );

  const [brand, setbrand] = useState([]);
  const [category, setcategory] = useState([]);
  const [subCategory, setSubCategory] = useState([]);

  const getBrandData = useCallback((search: any) => {
    searchProductBrands(search)
      .then((data) => {
        setbrand(data as never[]);
      })
      .catch((error) => {
        console.error(error?.response);
      });
  }, []);

  // Only the latest request may fill the list, so a slow reply for the
  // previous category never shows its sub categories under the new one.
  const subCategoryRequest = useRef(0);
  const getSubCategoryData = useCallback((search: string, categoryId: string) => {
    const request = ++subCategoryRequest.current;
    if (!categoryId) {
      setSubCategory([]);
      return;
    }
    searchProductSubCategories(search, categoryId)
      .then((data) => {
        if (request === subCategoryRequest.current) {
          setSubCategory(data as never[]);
        }
      })
      .catch((error) => {
        console.error(error?.response);
      });
  }, []);

  const getCategoryData = useCallback((search: string) => {
    searchProductCategories(search)
      .then((data) => {
        setcategory(data as never[]);
      })
      .catch((error) => {
        console.error(error?.response);
      });
  }, []);

  useEffect(() => {
    getBrandData("");
    getCategoryData("");
  }, [getBrandData, getCategoryData]);

  // The one place the sub category list follows the chosen category.
  useEffect(() => {
    getSubCategoryData("", formData.categoryId);
  }, [formData.categoryId, getSubCategoryData]);
  const getProductData = useCallback((id: string) => {
    findProductById(id)
      .then((data: any) => {
        setFormData((prev) => ({
          ...prev,
          brandId: data.brandId ?? "",
          ...productCategoryIds(data),
          productName: data.productName,
          alertQuantity: data.alertQuantity,
          price: data.price,
          wholePrice: data.price,
          scale: data.scale,
          packs: Array.isArray(data.packs) ? data.packs : [],
        }));
        const loaded: DoseLink | null = data.linkedDose
          ? {
              prescriptionId: data.linkedDose.prescriptionId,
              drugName: data.linkedDose.drugName,
              type: data.linkedDose.type,
              form: data.linkedDose.form,
              doseId: data.linkedDose.doseId,
              doseValue: data.linkedDose.doseValue,
            }
          : null;
        setLink(loaded);
        setSavedLink(loaded);
      })
      .catch((error) => {
        console.error(error?.response);
      });
  }, []);
  useEffect(() => {
    if (id) {
      getProductData(id);
    }

  }, [id, getProductData]);

  const searchBrand = (e: any) => {
    getBrandData(e);
  };

  const searchCategory = (e: string) => {
    getCategoryData(e);
  };

  const searchSubCategory = (e: string) => {
    getSubCategoryData(e, formData.categoryId);
  };

  const handleCategoryChange = useCallback(
    (e: {
      target: { name: string; value: string; valueTitle: string; value2: unknown };
    }) => {
      const { name, value } = e.target;
      // A new category drops the sub category, which belonged to the old one.
      if (name === "categoryId") {
        setFormData((prev) => ({
          ...prev,
          categoryId: value,
          subCategoryId: "",
        }));
        return;
      }
      handleChange(e);
    },
    [handleChange]
  );
  const addPack = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      packs: [...prev.packs, { name: "", unitsPerPack: "", isDefault: false }],
    }));
  }, []);

  const removePack = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      packs: prev.packs.filter((_, i) => i !== index),
    }));
  }, []);

  const updatePack = useCallback(
    (index: number, field: keyof Pack, value: string | boolean) => {
      setFormData((prev) => ({
        ...prev,
        // Only one pack can be the default, so ticking one clears the rest.
        packs:
          field === "isDefault"
            ? setDefaultPack(prev.packs, index)
            : prev.packs.map((pack, i) =>
                i === index ? { ...pack, [field]: value } : pack
              ),
      }));
    },
    []
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setloadeActive();
      const {
        brandId,
        categoryId,
        subCategoryId,
        productName,
        alertQuantity,
        quantity,
        wholePrice,
        scale,
        price,
        packs,
      } = formData;

      const packRows = filledPacks(packs);
      const badPack = findInvalidPack(packs);

      if (
        !productName ||
        !categoryId ||
        alertQuantity <= 0 ||
        quantity <= 0 ||
        // A price of 0 is allowed (free or not-for-sale items); negative is not.
        !(wholePrice >= 0) ||
        !scale ||
        !(price >= 0) ||
        badPack
      ) {
        setErrors(true);
        setloadeDeactive();
        return;
      }

      if (isMedicineScale(scale) && !link && !allowUnlinked.current) {
        setUnlinkedWarning(true);
        setloadeDeactive();
        return;
      }
      allowUnlinked.current = false;
      setUnlinkedWarning(false);

      // The link has its own endpoints on edit; they run after the product
      // saves, unlinking first because a product fills only one dose.
      const syncLink = async (productId: string) => {
        if (savedLink?.doseId === link?.doseId) return;
        if (savedLink) await unlinkProductFromDose(productId);
        if (link) await linkProductToDose(link.prescriptionId, productId, link.doseId);
      };

      const request = id
        ? updateProductById(id, {
            brandId,
            categoryId,
            subCategoryId,
            productName,
            alertQuantity,
            price,
            packs: packRows,
          }).then(() => syncLink(id))
        : createProduct({
            brandId,
            categoryId,
            subCategoryId,
            productName,
            alertQuantity,
            quantity,
            wholePrice,
            scale,
            price,
            packs: packRows,
            ...(link
              ? { linkDose: { prescriptionId: link.prescriptionId, doseId: link.doseId } }
              : {}),
          });

      request
        .then(() => {
          setloadeDeactive();
          closeModal();
        })
        .catch((error) => {
          console.error(error?.response);
          toast.error(
            error?.response?.data?.message || "Failed to save the product."
          );
          setloadeDeactive();
        });
    },
    [formData, closeModal, id, link, savedLink]
  );

  const modelClose = useCallback(() => {
    closeModal();
    setFormData((prev) => ({
      ...prev,
      brandId: "",
      categoryId: "",
      subCategoryId: "",
      productName: "",
      alertQuantity: 0,
      price: 0,
    }));
  }, [closeModal]);
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between">
          <h2 className="text-xs font-bold text-slate-800 dark:text-slate-100">
            {id ? "Update Product" : "Add Product"}
          </h2>
          <button
            onClick={modelClose}
            className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-3 space-y-3">
          <InputDropSearch
            data={category}
            name="categoryId"
            label={"Category"}
            selectName1="categoryName"
            selectValue="_id"
            value={formData.categoryId}
            onChange={handleCategoryChange}
            onSearch={searchCategory}
            required
            errors={errors && formData.categoryId === ""}
            errormessage="Category is required"
          />
          <InputDropSearch
            data={subCategory}
            name="subCategoryId"
            label={"Sub Category"}
            selectName1="subCategoryName"
            selectValue="_id"
            value={formData.subCategoryId}
            onChange={handleChange}
            onSearch={searchSubCategory}
            isDisable={!formData.categoryId}
            clearable
          />
          <InputDropSearch
            data={brand}
            name="brandId"
            label={"Brand Name"}
            selectName1="brandName"
            value={formData.brandId}
            selectValue="_id"
            onChange={handleChange}
            onSearch={searchBrand}
            clearable
          />

          <InputText
            onChange={handleChange}
            value={formData.productName}
            name="productName"
            required
            label={"Product Name"}
            errors={errors && formData.productName === ""}
          />
          <InputSelect
            option={scales}
            name="scale"
            required
            label={"Units of Measure"}
            value={formData.scale}
            onChange={handleChange}
            isDisable={!!id}
            errors={errors && formData.scale === ""}
          />

          <div className="grid grid-cols-2 gap-4">
            <InputText
              onChange={handleChange}
              value={formData.quantity}
              name="quantity"
              required
              type="number"
              label={`Units (${formData.scale || "unit"})`}
              errormessage={"Units must be greater than zero."}
              errors={errors && formData.quantity <= 0}
            />
            <InputText
              onChange={handleChange}
              value={formData.wholePrice}
              name="wholePrice"
              required
              type="number"
              label={"Price"}
              errormessage={"Price can't be negative."}
              errors={errors && !(formData.wholePrice >= 0)}
            />
          </div>

          <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/40 rounded-lg">
            <p className="text-xs text-slate-600 dark:text-slate-300">
              Price per 1 {formData.scale || "unit"}:{" "}
              <span className="font-bold text-indigo-600 dark:text-indigo-300">
                {formData.price.toFixed(2)}
              </span>
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 dark:border-slate-600 p-2.5 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                  Purchase packs
                </p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                  Optional. How this product is bought, in {formData.scale || "base units"}.
                </p>
              </div>
              <button
                type="button"
                onClick={addPack}
                className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-[11px] font-medium shrink-0"
              >
                + Add pack
              </button>
            </div>

            {formData.packs.length === 0 ? (
              <p className="text-[11px] text-slate-400 dark:text-slate-500 py-1">
                No packs - bought in {formData.scale || "base units"} only.
              </p>
            ) : (
              <div className="space-y-1.5">
                {formData.packs.map((pack, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <input
                      value={pack.name}
                      onChange={(e) => updatePack(i, "name", e.target.value)}
                      placeholder="Card"
                      className={`w-24 h-9 px-2 rounded-md border bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs outline-none focus:ring-2 focus:ring-indigo-500 ${
                        errors && pack.name.trim() === ""
                          ? "border-red-500"
                          : "border-slate-300 dark:border-slate-600"
                      }`}
                    />
                    <span className="text-[11px] text-slate-400 shrink-0">=</span>
                    <input
                      value={pack.unitsPerPack}
                      onChange={(e) =>
                        updatePack(i, "unitsPerPack", e.target.value.replace(/[^0-9]/g, ""))
                      }
                      placeholder="12"
                      inputMode="numeric"
                      className={`w-16 h-9 px-2 rounded-md border bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs outline-none focus:ring-2 focus:ring-indigo-500 ${
                        errors && Number(pack.unitsPerPack) < 2
                          ? "border-red-500"
                          : "border-slate-300 dark:border-slate-600"
                      }`}
                    />
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                      {formData.scale || "unit"}
                    </span>
                    <label className="flex items-center gap-1 ml-auto text-[11px] text-slate-600 dark:text-slate-300 cursor-pointer shrink-0">
                      <input
                        type="radio"
                        name="defaultPack"
                        checked={pack.isDefault}
                        onChange={() => updatePack(i, "isDefault", true)}
                        className="accent-indigo-600"
                      />
                      default
                    </label>
                    <button
                      type="button"
                      onClick={() => removePack(i)}
                      className="w-7 h-7 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center shrink-0"
                      title="Remove pack"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                ))}
                <p className="text-[10px] text-slate-400 dark:text-slate-500">
                  A pack must hold 2 or more {formData.scale || "units"}.
                </p>
              </div>
            )}
          </div>

          <InputText
            onChange={handleChange}
            value={formData.alertQuantity}
            name="alertQuantity"
            required
            label={`Alert Quantity (${formData.scale || "unit"})`}
            type="number"
            errormessage={"Quantity must be greater than zero."}
            errors={errors && formData.alertQuantity <= 0}
          />

          <div ref={pickerRef}>
            <PrescriptionLinkPicker
              scale={formData.scale}
              value={link}
              onChange={(next) => {
                setLink(next);
                if (next) setUnlinkedWarning(false);
              }}
            />
          </div>

          {unlinkedWarning && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-2.5 space-y-2">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                Not linked to a prescription drug, so prescriptions can't bill
                it from stock.
              </p>
              <div className="flex justify-end gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setUnlinkedWarning(false);
                    pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  className="px-2.5 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors text-[11px] font-medium"
                >
                  Link now
                </button>
                <button
                  type="button"
                  onClick={() => {
                    allowUnlinked.current = true;
                    formRef.current?.requestSubmit();
                  }}
                  className="px-2.5 py-1.5 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors text-[11px] font-medium"
                >
                  Save anyway
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-600">
            <button
              type="button"
              onClick={modelClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
            >
              {id ? "Update" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default React.memo(AddProduct);
